import { useState, useEffect, useContext } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Table, Column } from '@appkit4/react-components/table';
import { Tooltip } from '@appkit4/react-components/tooltip';
import { Panel } from '@appkit4/react-components/panel';
import { Notification, toaster } from '@appkit4/react-components/notification';
import RemoveAdminModal from '@components/RemoveAdminModal/RemoveAdminModal';
import AddAdminModal from '@components/AddAdminModal';
import SuccessModal from '@components/SuccessModal';
import { UserContext } from '@contexts/UserProvider';
import { isUserAdmin } from '@utils/users';
import { listAdmins, removeAdminRole } from '@config/getAdminApis';
import styles from './ManageAdmins.module.scss';

interface AdminUser {
    id: string;
    name: string;
    email: string;
    role: string;
}

const ManageAdmins = () => {
    const { user } = useContext(UserContext);
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const adminUsers = await listAdmins();
            const formattedAdmins = adminUsers.map((user, index) => ({
                id: `${index}`,
                name: user.fullName,
                email: user.email,
                role: 'Admin',
            }));
            setAdmins(formattedAdmins);
        } catch (error) {
            console.error('Error fetching admins:', error);
            toaster.notify(<Notification status="error" title="Error" message="Failed to load admins list" />);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const handleAddAdmin = () => {
        // Check if current user is admin
        if (!isUserAdmin(user)) {
            toaster.notify(
                <Notification status="error" title="Access Denied" message="Only administrators can add admins" />
            );
            return;
        }
        setAddModalVisible(true);
    };

    const handleAddModalCancel = () => {
        setAddModalVisible(false);
    };

    const handleAdminAdded = async () => {
        await fetchAdmins();
    };

    const handleRemoveClick = (admin: AdminUser) => {
        setSelectedAdmin(admin);
        setRemoveModalVisible(true);
    };

    const handleRemoveConfirm = async () => {
        // Check if current user is admin
        if (!isUserAdmin(user)) {
            toaster.notify(
                <Notification status="error" title="Access Denied" message="Only administrators can remove admins" />
            );
            return;
        }

        if (selectedAdmin) {
            try {
                const result = await removeAdminRole(selectedAdmin.email);
                if (result.success) {
                    setRemoveModalVisible(false);
                    setSuccessModalVisible(true);
                    await fetchAdmins();
                } else {
                    toaster.notify(<Notification status="error" title="Error" message="Failed to remove admin" />);
                }
            } catch (error) {
                console.error('Error removing admin:', error);
                toaster.notify(<Notification status="error" title="Error" message="Failed to remove admin" />);
            }
        }
    };

    const handleRemoveCancel = () => {
        setRemoveModalVisible(false);
        setSelectedAdmin(null);
    };

    const handleSuccessModalClose = () => {
        setSuccessModalVisible(false);
        setSelectedAdmin(null);
    };

    const renderActionsCell = (row: AdminUser) => {
        return (
            <div className={styles['action-cell']}>
                <Tooltip trigger="hover" position="top" distance={4} content="Remove admin">
                    <button
                        className={styles['remove-icon-button']}
                        onClick={() => handleRemoveClick(row)}
                        aria-label={`Remove ${row.name}`}>
                        <span className="Appkit4-icon icon-account-remove-outline"></span>
                    </button>
                </Tooltip>
            </div>
        );
    };

    return (
        <Panel className={styles['manage-admins']}>
            <div className={styles['header-section']}>
                <h1 className={styles['page-title']}>Manage admins</h1>
                <Button
                    kind="primary"
                    icon="icon-add-user-outline"
                    onClick={handleAddAdmin}
                    className={styles['add-button']}>
                    Add admin
                </Button>
            </div>

            <div className={styles['table-container']}>
                {loading ? (
                    <div className={styles['loading-state']}>Loading admins...</div>
                ) : (
                    <Table originalData={admins} hasTitle striped>
                        <Column field="name" className={styles['name-column']}>
                            Name
                        </Column>
                        <Column field="email" className={styles['email-column']}>
                            Email
                        </Column>
                        <Column field="role" className={styles['role-column']}>
                            Role
                        </Column>
                        <Column field="id" className={styles['actions-column']} renderCell={renderActionsCell}>
                            Actions
                        </Column>
                    </Table>
                )}
            </div>

            {removeModalVisible && selectedAdmin && (
                <RemoveAdminModal
                    visible={removeModalVisible}
                    title="Remove Admin"
                    adminName={selectedAdmin.name}
                    onCancel={handleRemoveCancel}
                    onRemoveConfirm={handleRemoveConfirm}
                />
            )}

            <AddAdminModal
                visible={addModalVisible}
                onCancel={handleAddModalCancel}
                onAdminAdded={handleAdminAdded}
                existingAdmins={admins.map((admin) => admin.email)}
            />

            <SuccessModal
                visible={successModalVisible}
                title="Admin access removed"
                message="Access to Design Draft remains unchanged."
                onClose={handleSuccessModalClose}
            />
        </Panel>
    );
};

export default ManageAdmins;
